/**
 * useAuth — WorkOS redirect-based auth hook for a Vite React SPA.
 *
 * Flow:
 *   1. login()  →  GET /auth/login  →  redirect to WorkOS
 *   2. WorkOS   →  redirect back to /auth/callback?code=…
 *   3. hook detects ?code=, POSTs /auth/callback, stores tokens
 *   4. isAuthenticated becomes true; user info is available
 */
import { useState, useEffect, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import {
  getStoredUser,
  storeSession,
  clearSession,
  markIntentionalLogout,
  type AuthUser,
} from '@/lib/auth'
import { API_BASE, apiFetch } from '@/lib/apiFetch'
import { capture, identifyUser, resetUser } from '@/lib/analytics'

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser)
  const [isLoading, setIsLoading] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)

  // Sync user identity to Sentry whenever session state changes.
  // Covers: app mount with stored session, login callback success, logout,
  // and forced logout (401-after-refresh) which triggers a page reload + null user.
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email })
      identifyUser(user)
    } else {
      Sentry.setUser(null)
      resetUser()
    }
  }, [user])

  // Handle the OAuth callback (?code=… in the URL)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (!code) return

    /* eslint-disable react-hooks/set-state-in-effect -- one-shot OAuth callback handler: mount-time side effect that exchanges ?code for a session */
    setIsLoading(true)
    setAuthError(null)
    /* eslint-enable react-hooks/set-state-in-effect */

    fetch(`${API_BASE}/auth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Auth failed: HTTP ${r.status}`)
        return r.json() as Promise<{
          access_token: string
          refresh_token: string
          user: AuthUser
        }>
      })
      .then(({ access_token, refresh_token, user: u }) => {
        storeSession(access_token, u, refresh_token)
        setUser(u)
        // Fires once per genuine login (only runs on a real ?code= OAuth
        // return), unlike identifyUser() which re-runs on every app load with a
        // stored session. Email is passed as a property so downstream alerts
        // (e.g. PostHog→Slack) don't depend on identify() resolution timing.
        capture('user_logged_in', { email: u.email })
        // Remove the code from the URL without a full reload
        window.history.replaceState({}, '', window.location.pathname)
      })
      .catch((err: unknown) => {
        setAuthError(err instanceof Error ? err.message : 'Authentication failed')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const login = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/login`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const { url } = (await res.json()) as { url: string }
      window.location.href = url
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Could not reach login service')
    }
  }, [])

  const logout = useCallback(async () => {
    // Fetch the WorkOS hosted-logout URL, then hand the browser to it in a
    // single top-level navigation. Visiting it ends the session AND clears
    // WorkOS's own SSO cookie — without that, the next "Sign in" silently
    // re-authenticates the same identity (bad on shared devices). Best-effort
    // with a short timeout: a slow or down auth provider must not trap users.
    let logoutUrl: string | null = null
    try {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), 2000)
      const res = await apiFetch('/auth/logout', { method: 'POST', signal: controller.signal })
      clearTimeout(timer)
      if (res.ok) {
        logoutUrl = ((await res.json()) as { logout_url: string | null }).logout_url
      }
    } catch {
      // Intentional: proceed regardless — the fallback below still signs out.
    }
    clearSession()

    if (logoutUrl) {
      // Standard hosted logout. Deliberately do NOT touch React state here:
      // the page is leaving, and flipping `isAuthenticated` to false would
      // wake App's auto-redirect-to-login effect, firing a SECOND navigation
      // to /authorize that races this one. Whenever that race is lost — or the
      // hosted logout is skipped — the still-live SSO cookie silently re-auths
      // the user (the "had to click logout twice" bug). One unconditional
      // redirect, and nothing else, is what lets WorkOS clear the cookie.
      window.location.href = logoutUrl
      return
    }

    // No hosted-logout URL: the token carried no session id, or /auth/logout
    // was unreachable. We could not clear WorkOS's SSO cookie, so an automatic
    // /authorize would silently sign the user back in. Mark the logout as
    // intentional so App shows an explicit "Sign in" screen instead of
    // auto-redirecting, then reload to a clean state.
    markIntentionalLogout()
    window.location.href = '/'
  }, [])

  return {
    user,
    isLoading,
    authError,
    isAuthenticated: !!user,
    login,
    logout,
  }
}
